import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { 
  Users, 
  Search, 
  Mail,
  Building2,
  ShieldCheck,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { collection, getDocs, collectionGroup } from 'firebase/firestore'

interface UserOrganization {
  id: string
  name: string
  slug: string
  role: string
}

interface User {
  id: string
  email: string
  displayName?: string
  photoURL?: string
  createdAt?: Date
  lastLoginAt?: Date
  isSuperAdmin?: boolean
  organizations: UserOrganization[]
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const db = getFirebaseDb()
        
        // Fetch all memberships via collectionGroup - this is our source of truth for users
        const membersSnapshot = await getDocs(collectionGroup(db, 'members'))
        
        // Build a map of userId -> memberships
        const userMemberships = new Map<string, { orgId: string; role: string; userId: string }[]>()
        const allUserIds = new Set<string>()
        
        membersSnapshot.docs.forEach(docSnap => {
          const data = docSnap.data()
          const userId = data.userId || docSnap.id
          const orgId = docSnap.ref.parent.parent?.id
          
          allUserIds.add(userId)
          
          if (orgId) {
            const memberships = userMemberships.get(userId) || []
            memberships.push({ orgId, role: data.role || 'member', userId })
            userMemberships.set(userId, memberships)
          }
        })
        
        // Also check the users collection for any additional user data
        const usersSnapshot = await getDocs(collection(db, 'users'))
        const usersDataMap = new Map<string, { email?: string; displayName?: string; photoURL?: string; role?: string; createdAt?: Date; lastLoginAt?: Date }>()
        
        usersSnapshot.docs.forEach(docSnap => {
          const data = docSnap.data()
          allUserIds.add(docSnap.id)
          usersDataMap.set(docSnap.id, {
            email: data.email,
            displayName: data.displayName,
            photoURL: data.photoURL,
            role: data.role,
            createdAt: data.createdAt?.toDate(),
            lastLoginAt: data.lastLoginAt?.toDate(),
          })
        })
        
        // Fetch all organizations for lookup
        const orgsSnapshot = await getDocs(collection(db, 'organizations'))
        const orgsMap = new Map<string, { name: string; slug: string }>()
        orgsSnapshot.docs.forEach(docSnap => {
          const data = docSnap.data()
          orgsMap.set(docSnap.id, { name: data.name, slug: data.slug })
        })
        
        // Build users list from all discovered user IDs
        const usersData: User[] = Array.from(allUserIds).map(userId => {
          const userData = usersDataMap.get(userId)
          const memberships = userMemberships.get(userId) || []
          
          // Map memberships to organizations
          const organizations: UserOrganization[] = memberships
            .map(m => {
              const org = orgsMap.get(m.orgId)
              if (!org) return null
              return {
                id: m.orgId,
                name: org.name,
                slug: org.slug,
                role: m.role
              }
            })
            .filter((o): o is UserOrganization => o !== null)
          
          return {
            id: userId,
            email: userData?.email || `User ${userId.substring(0, 8)}...`,
            displayName: userData?.displayName,
            photoURL: userData?.photoURL,
            createdAt: userData?.createdAt,
            lastLoginAt: userData?.lastLoginAt,
            isSuperAdmin: userData?.role === 'super-admin',
            organizations
          }
        })
        
        // Sort by email
        usersData.sort((a, b) => a.email.localeCompare(b.email))
        setUsers(usersData)
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchUsers()
  }, [])

  const toggleExpanded = (userId: string) => {
    const newExpanded = new Set(expandedUsers)
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId)
    } else {
      newExpanded.add(userId)
    }
    setExpandedUsers(newExpanded)
  }

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.organizations.some(org => 
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.slug.toLowerCase().includes(searchQuery.toLowerCase())
    )
  )

  const getInitials = (user: User) => {
    if (user.displayName) {
      return user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }
    return user.email.slice(0, 2).toUpperCase()
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Never'
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-muted-foreground">
            {users.length} users on the platform
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by email, name, or organization..."
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.isSuperAdmin).length}</p>
                <p className="text-sm text-muted-foreground">Super Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.organizations.length > 0).length}</p>
                <p className="text-sm text-muted-foreground">With Organizations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Users Found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {searchQuery 
                  ? 'No users match your search' 
                  : 'No users have been created yet'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredUsers.map((user) => {
            const isExpanded = expandedUsers.has(user.id)
            
            return (
              <Card key={user.id} className="overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleExpanded(user.id)}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.photoURL} />
                      <AvatarFallback>{getInitials(user)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {user.displayName || user.email.split('@')[0]}
                        </span>
                        {user.isSuperAdmin && (
                          <Badge variant="default" className="text-xs">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Super Admin
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Mail className="h-3 w-3" />
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm text-muted-foreground hidden md:block">
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {user.organizations.length} org{user.organizations.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="border-t bg-muted/30 p-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* User Details */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">User Details</h4>
                        <div className="text-sm space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">User ID</span>
                            <code className="text-xs bg-muted px-2 py-1 rounded">{user.id}</code>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Created</span>
                            <span>{formatDate(user.createdAt)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Last Login</span>
                            <span>{formatDate(user.lastLoginAt)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Organizations */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">Organizations</h4>
                        {user.organizations.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No organizations</p>
                        ) : (
                          <div className="space-y-2">
                            {user.organizations.map((org) => (
                              <div 
                                key={org.id} 
                                className="flex items-center justify-between text-sm bg-background rounded-md p-2"
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                  <Link 
                                    to={`/${org.slug}/assist`}
                                    className="hover:underline"
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  >
                                    {org.name}
                                  </Link>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    {org.role}
                                  </Badge>
                                  <Link 
                                    to={`/admin/organizations/${org.id}`}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  >
                                    <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
